import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { useOrganization } from '../../context/OrganizationContext';
import { fetchWithOrg } from '../../utils/api';
import MCPProviders from '../../components/MCPProviders';

import {
  CheckCircle,
  CircleNotch,
  FloppyDisk,
  WarningCircle
} from '@phosphor-icons/react';
/**
 * Admin Integrations Settings - Phase 11.2
 * Teams configuration (Phase 12 stub)
 * MCP Providers management
 */
const IntegrationsSettings = () => {
  const { activeOrganization } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Teams settings
  const [teamsEnabled, setTeamsEnabled] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  useEffect(() => {
    if (activeOrganization) {
      fetchIntegrations();
    }
  }, [activeOrganization]);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/admin/integrations`,
        {},
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        const teams = data.integrations?.microsoft_teams || {};
        setTeamsEnabled(teams.enabled || false);
        setTenantId(teams.tenant_id || '');
        setClientId(teams.client_id || '');
        // Never fetch client_secret for security
      }
    } catch (err) {
      console.error('Error fetching integrations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const payload = {
        microsoft_teams: {
          enabled: teamsEnabled,
          tenant_id: tenantId,
          client_id: clientId,
          status: (teamsEnabled && tenantId && clientId) ? 'configured' : 'not_configured'
        }
      };

      // Only include client_secret if provided
      if (clientSecret) {
        payload.microsoft_teams.client_secret = clientSecret;
      }

      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/admin/integrations`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        },
        activeOrganization.organization_id
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'Integration settings saved successfully' });
        setClientSecret(''); // Clear client secret after save
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving integrations:', err);
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-6">
        <div className="max-w-4xl mx-auto">
          <div className="h-8 bg-gray-700 rounded w-64 mb-8 animate-pulse"></div>
          <div className="glass rounded-xl p-6 h-96 animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Integrations</h1>
          <p className="text-gray-400">Configure external service integrations for your organization</p>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg border flex items-start gap-3 ${
            message.type === 'success'
              ? 'bg-green-500/10 border-green-500/30'
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
            ) : (
              <WarningCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
            )}
            <p className={`text-sm ${
              message.type === 'success' ? 'text-green-300' : 'text-red-300'
            }`}>
              {message.text}
            </p>
          </div>
        )}

        {/* Microsoft Teams Integration */}
        <div className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)]">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-white mb-2">Microsoft Teams</h2>
              <p className="text-sm text-gray-400">
                Enable Teams meeting capture and bot integration
              </p>
            </div>
            
            {/* Enable Toggle */}
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={teamsEnabled}
                onChange={(e) => setTeamsEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#6366F1]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#6366F1]"></div>
            </label>
          </div>

          {/* Connection Status */}
          {teamsEnabled && tenantId && clientId && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-400" />
                <p className="text-sm text-green-300">
                  <strong>Teams Integration Active:</strong> Your organization can now capture Teams meeting data through agents.
                </p>
              </div>
            </div>
          )}

          {teamsEnabled && (!tenantId || !clientId) && (
            <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <div className="flex items-center gap-2">
                <WarningCircle size={16} className="text-yellow-400" />
                <p className="text-sm text-yellow-300">
                  <strong>Configuration Incomplete:</strong> Please provide Tenant ID, Client ID, and Client Secret to enable Teams integration.
                </p>
              </div>
            </div>
          )}

          {/* Configuration Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Tenant ID
              </label>
              <input
                type="text"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                disabled={!teamsEnabled}
                className="w-full px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#6366F1] disabled:opacity-50"
              />
              <p className="mt-1 text-xs text-gray-500">
                Find your Tenant ID in Azure Active Directory
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Client ID (Application ID)
              </label>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                disabled={!teamsEnabled}
                className="w-full px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#6366F1] disabled:opacity-50"
              />
              <p className="mt-1 text-xs text-gray-500">
                Create an app registration in Azure Portal
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Client Secret
              </label>
              <input
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder="Enter new secret (leave blank to keep existing)"
                disabled={!teamsEnabled}
                className="w-full px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#6366F1] disabled:opacity-50"
              />
              <p className="mt-1 text-xs text-gray-500">
                Client secret is stored securely and never displayed
              </p>
            </div>
          </div>

          {/* FloppyDisk Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <CircleNotch size={18} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FloppyDisk size={18} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>

        {/* MCP Providers Section */}
        <div className="mt-8 glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)]">
          <MCPProviders />
        </div>
      </div>
    </div>
  );
};

export default IntegrationsSettings;